import { BellOff, Forum, Hash, ServerCrash } from '@/components/ui/icons';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import type { ChannelDTO } from '@clanfire/shared';
import { ChatView } from '@/components/chat/ChatView';
import { MemberList } from '@/components/members/MemberList';
import { SearchPanel } from '@/components/search/SearchPanel';
import { useMemberCounts } from '@/components/layout/ChannelSidebar';
import { RightPanel, TopBar } from '@/components/layout/TopBar';
import { VoiceBanner } from '@/components/voice/VoiceBanner';
import { VoiceChannelView } from '@/components/voice/VoiceChannelView';
import { Button, IconButton } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/Feedback';
import { useIsWide, useMediaQuery } from '@/hooks/useAppEffects';
import { threadApi } from '@/api/endpoints';
import { ThreadPanel } from '@/components/chat/ThreadPanel';
import { ThreadsButton } from '@/components/chat/ThreadsButton';
import { Spinner } from '@/components/ui/Spinner';
import { threadPath, useThread, useThreads } from '@/stores/threads';
import { useChannelPermissions, useMyPermissions } from '@/lib/permissions';
import { setChannelMuted } from '@/lib/readState';
import { useReadStates } from '@/stores/social';
import { useServerChannels, useServers } from '@/stores/servers';
import { useUi } from '@/stores/ui';
import { PinsButton } from '@/components/chat/PinsButton';
import { ForumView } from '@/components/forum/ForumView';
import { RulesBanner } from '@/components/server/RulesGate';

export function ServerPage() {
  const { serverId = '', channelId } = useParams();
  const server = useServers((s) => s.servers[serverId]);
  const channels = useServerChannels(serverId);
  const rememberChannel = useUi((s) => s.rememberChannel);
  const channel = channels.find((c) => c.id === channelId);

  useEffect(() => {
    if (channel?.type === 'TEXT') rememberChannel(serverId, channel.id);
  }, [channel?.id, channel?.type, serverId, rememberChannel]);

  if (!server) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          icon={<ServerCrash size={28} />}
          title="Server not found"
          body="It may have been deleted, or you're no longer a member."
          action={
            <Link to="/app/@me">
              <Button>Go home</Button>
            </Link>
          }
        />
      </div>
    );
  }

  if (!channel && channelId) {
    // Links to a thread's messages (notifications, search) use the thread's id as the channel.
    return <ThreadRedirect key={channelId} serverId={serverId} threadId={channelId} fallback={<ChannelFallback serverId={serverId} />} />;
  }
  if (!channel) return <ChannelFallback serverId={serverId} />;

  if (channel.type === 'FORUM') return <ForumChannelPage channel={channel} />;
  return channel.type === 'TEXT' ? <TextChannelPage channel={channel} /> : <VoiceChannelPage channel={channel} />;
}

/** Sends you to a thread beside its channel, or on to the server's default channel when it isn't one. */
function ThreadRedirect({ serverId, threadId, fallback }: { serverId: string; threadId: string; fallback: React.ReactNode }) {
  const known = useThread(threadId);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (known) return;
    threadApi
      .get(threadId)
      .then((t) => useThreads.getState().upsert([t]))
      .catch(() => setMissing(true));
  }, [threadId, known]);
  if (known && known.serverId === serverId) return <Navigate to={threadPath(known)} replace />;
  if (missing || known) return fallback;
  return (
    <div className="flex h-full items-center justify-center text-fg-muted">
      <Spinner />
    </div>
  );
}

/** Opens the channel you were last in (or the first one), or says there are none. */
function ChannelFallback({ serverId }: { serverId: string }) {
  const server = useServers((s) => s.servers[serverId])!;
  const channels = useServerChannels(serverId);
  const lastChannel = useUi((s) => s.lastChannelByServer[serverId]);
  const openModal = useUi((s) => s.openModal);
  const perms = useMyPermissions(serverId);
  const fallback = channels.find((c) => c.id === lastChannel && c.type === 'TEXT') ?? channels.find((c) => c.type === 'TEXT') ?? channels[0];
  if (fallback) return <Navigate to={`/app/${serverId}/${fallback.id}`} replace />;
  return (
    <div className="flex h-full flex-col">
      <TopBar title={server.name} showSearch={false} />
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          art="channels"
          title="No channels yet"
          body="This server doesn't have any channels."
          action={perms.can('MANAGE_CHANNELS') ? <Button onClick={() => openModal({ type: 'createChannel', serverId, channelType: 'TEXT' })}>Create a channel</Button> : undefined}
        />
      </div>
    </div>
  );
}

function ServerRightPanel({ serverId, channel }: { serverId: string; channel: ChannelDTO }) {
  const membersOpen = useUi((s) => s.membersOpen);
  const searchOpen = useUi((s) => s.searchOpen);
  const setMembersOpen = useUi((s) => s.setMembersOpen);
  const setSearchOpen = useUi((s) => s.setSearchOpen);
  const wide = useIsWide();

  if (searchOpen) {
    return (
      <RightPanel onClose={() => setSearchOpen(false)}>
        <SearchPanel scope={{ serverId, channelId: channel.type === 'TEXT' ? channel.id : undefined }} onClose={() => setSearchOpen(false)} />
      </RightPanel>
    );
  }
  // On narrow screens the member list starts hidden and opens as an overlay.
  if (!membersOpen) return null;
  return (
    <RightPanel onClose={() => setMembersOpen(false)}>
      <MemberList serverId={serverId} onClose={wide ? undefined : () => setMembersOpen(false)} />
    </RightPanel>
  );
}

function TextChannelPage({ channel }: { channel: ChannelDTO }) {
  const perms = useChannelPermissions(channel.id);
  const serverName = useServers((s) => s.servers[channel.serverId]?.name ?? '');
  const counts = useMemberCounts(channel.serverId);
  const { threadId } = useParams();
  const thread = useThread(threadId);
  const navigate = useNavigate();
  // Wide enough to show the thread beside the channel; otherwise it takes the channel's place.
  const split = useMediaQuery('(min-width: 1024px)');
  const closeThread = () => navigate(`/app/${channel.serverId}/${channel.id}`);

  // Opening a thread link before the thread is known (or after it's gone).
  useEffect(() => {
    if (!threadId || thread) return;
    let cancelled = false;
    threadApi
      .get(threadId)
      .then((t) => !cancelled && useThreads.getState().upsert([t]))
      .catch(() => !cancelled && navigate(`/app/${channel.serverId}/${channel.id}`, { replace: true }));
    return () => {
      cancelled = true;
    };
  }, [threadId, thread, channel.id, channel.serverId, navigate]);

  if (thread && thread.parentId !== channel.id) return <Navigate to={threadPath(thread)} replace />;
  if (thread && !split) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <ThreadPanel thread={thread} onClose={closeThread} asPage />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar
        icon={<Hash size={20} />}
        iconOnPhones={false}
        title={channel.name}
        subtitle={channel.topic}
        mobileSubtitle={counts ? `${serverName} · ${counts.online} online` : serverName}
        searchLabel={`Search ${serverName}`}
        showMembersToggle
      >
        <ThreadsButton channelId={channel.id} canCreate={perms.can('SEND_MESSAGES')} />
        <PinsButton target={{ channelId: channel.id }} canPin={perms.can('MANAGE_MESSAGES')} />
        <MuteButton channelId={channel.id} />
      </TopBar>
      <VoiceBanner serverId={channel.serverId} />
      <RulesBanner serverId={channel.serverId} />
      <div className="flex min-h-0 flex-1">
        <ChatView key={channel.id} target={{ channelId: channel.id }} serverId={channel.serverId} className="min-w-0 flex-1" />
        {thread ? (
          <ThreadPanel thread={thread} onClose={closeThread} />
        ) : (
          <ServerRightPanel serverId={channel.serverId} channel={channel} />
        )}
      </div>
    </div>
  );
}

/** A forum: its posts, with an open post beside them (or in their place on narrow screens). */
function ForumChannelPage({ channel }: { channel: ChannelDTO }) {
  const serverName = useServers((s) => s.servers[channel.serverId]?.name ?? '');
  const { threadId } = useParams();
  const thread = useThread(threadId);
  const navigate = useNavigate();
  const split = useMediaQuery('(min-width: 1024px)');
  const closeThread = () => navigate(`/app/${channel.serverId}/${channel.id}`);

  useEffect(() => {
    if (!threadId || thread) return;
    let cancelled = false;
    threadApi
      .get(threadId)
      .then((t) => !cancelled && useThreads.getState().upsert([t]))
      .catch(() => !cancelled && navigate(`/app/${channel.serverId}/${channel.id}`, { replace: true }));
    return () => {
      cancelled = true;
    };
  }, [threadId, thread, channel.id, channel.serverId, navigate]);

  if (thread && thread.parentId !== channel.id) return <Navigate to={threadPath(thread)} replace />;
  if (thread && !split) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <ThreadPanel thread={thread} onClose={closeThread} asPage />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar icon={<Forum size={20} />} iconOnPhones={false} title={channel.name} subtitle="Forum" mobileSubtitle={serverName} searchLabel={`Search ${serverName}`} showMembersToggle />
      <VoiceBanner serverId={channel.serverId} />
      <RulesBanner serverId={channel.serverId} />
      <div className="flex min-h-0 flex-1">
        <ForumView channel={channel} openThreadId={thread?.id} />
        {thread ? <ThreadPanel thread={thread} onClose={closeThread} /> : <ServerRightPanel serverId={channel.serverId} channel={channel} />}
      </div>
    </div>
  );
}

function VoiceChannelPage({ channel }: { channel: ChannelDTO }) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar title={channel.name} subtitle="Voice channel" showMembersToggle />
      <RulesBanner serverId={channel.serverId} />
      <div className="flex min-h-0 flex-1">
        <VoiceChannelView channel={channel} />
        <ServerRightPanel serverId={channel.serverId} channel={channel} />
      </div>
    </div>
  );
}

/** Mutes the channel for you (no unread dot; mentions still come through). Also on the channel's right-click menu. */
function MuteButton({ channelId }: { channelId: string }) {
  const muted = useReadStates((s) => !!s.channels[channelId]?.muted);
  return (
    <IconButton label={muted ? 'Unmute channel' : 'Mute channel'} active={muted} aria-pressed={muted} onClick={() => void setChannelMuted(channelId, !muted)}>
      <BellOff size={20} />
    </IconButton>
  );
}
